import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { tileService } from "../../../services/enhancedCrudService";
import { toast } from "react-toastify";
import { MAX_PDF_FILE_SIZE } from "./../../../shared/constant";
import Swal from "sweetalert2";

const TilePdfs = () => {
  const MAX_PRODUCT_PDF_COUNT = 1;
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { product_id } = useParams();
  const [tile, setTile] = useState(null);
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [defaultFileName, setDefaultFileName] = useState("");
  const [fileName, setFileName] = useState("");
  const [hideFirm, setHideFirm] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [activeSection, setActiveSection] = useState("pdf"); // "pdf" or "link"

  const fetchTile = async () => {
    try {
      const response = await tileService.getDetail(product_id);
      setTile(response);
      const user = sessionStorage.getItem("user");
      const profile = JSON.parse(user);
      if (profile && profile.seller_profile) {
        const firmName = hideFirm ? "" : profile.seller_profile.firm_name;
        setDefaultFileName(
          response.tile_type_name +
            " " +
            response.tile_size_name +
            " " +
            response.tile_grade +
            (firmName ? " " + firmName : "") +
            " " +
            response.name
        );
      }
    } catch (error) {
      console.error("Error fetching tile:", error);
    }
  };

  const fetchPdfs = async () => {
    try {
      setLoading(true);
      const response = await tileService.getPdfs(product_id);
      setPdfs(response.pdfs || []);
    } catch (error) {
      console.error("Error fetching PDFs:", error);
      toast.error("Failed to load PDFs");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      if (pdfs.length + files.length > MAX_PRODUCT_PDF_COUNT) {
        toast.error(
          `Cannot upload ${files.length} files. Maximum ${MAX_PRODUCT_PDF_COUNT} PDFs allowed. Current count: ${pdfs.length}`
        );
        e.target.value = "";
        return;
      }
      const validFiles = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type !== "application/pdf") {
          toast.error(`File "${file.name}" is not a valid PDF file`);
          continue;
        }
        const maxSize = MAX_PDF_FILE_SIZE * 1024 * 1024;
        if (file.size > maxSize) {
          toast.error(
            `File "${file.name}" size should be less than ${MAX_PDF_FILE_SIZE} MB`
          );
          continue;
        }

        validFiles.push(file);
      }

      if (validFiles.length > 0) {
        setSelectedFile(validFiles[0]);
      } else {
        setSelectedFile(null);
        e.target.value = "";
      }
    }
  };


  const handleLinkIconClick = () => {
    setLinkUrl(tile?.link || "");
  };

  const saveLink = async () => {
    if (!linkUrl.trim()) {
      toast.error("Please enter a valid link URL");
      return;
    }
    try {
      new URL(linkUrl);
    } catch {
      toast.error("Please enter a valid URL (e.g., https://example.com)");
      return;
    }

    try {
      setUploading(true);
      const result = await tileService.addUpdateLink(product_id, {
        link: linkUrl.trim(),
      });      
      try {
        await tileService.updatePublishStatus(product_id, {
          action: "publish",
        });
        toast.success("Link saved and tile published successfully");
      } catch (publishError) {
        console.error("Error publishing tile:", publishError);
        toast.success("Link saved successfully, but failed to publish tile");
      }
      handleCloseLinkModal();
      navigate("/products/tiles");
    } catch (error) {
      console.error("Error saving link:", error);
      console.error("Error response:", error?.response);
      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save link";
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLink = async () => {
    const result = await Swal.fire({
      title: "Delete Link?",
      text: "This action will permanently delete the link.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await tileService.deleteLink(product_id);
        toast.success(response.message || "Link deleted successfully");
        navigate("/products/tiles");
      } catch (error) {
        console.error("Error deleting link:", error);
        const errorMsg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Failed to delete link";
        toast.error(errorMsg);
      }
    }
  };

  const handleCloseLinkModal = () => {
    setLinkUrl("");
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
    // Reset states when switching sections
    if (section === "pdf") {
      setLinkUrl("");
      setSelectedFile(null);
      setFileName("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } else if (section === "link") {
      setSelectedFile(null);
      setFileName("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const saveUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a PDF file to upload");
      return;
    }
    if (!fileName) {
      toast.error("Please enter a file name");
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("name", defaultFileName + " " + fileName + ".pdf");
      formData.append("firm_name_hidden", hideFirm);
      const result = await tileService.uploadPdf(product_id, formData);
      if (result.status == "success") {
        try {
          await tileService.updatePublishStatus(product_id, {
            action: "publish",
          });
          toast.success("PDF uploaded and tile published successfully");
        } catch (publishError) {
          console.error("Error publishing tile:", publishError);
          toast.success(
            "PDF uploaded successfully, but failed to publish tile"
          );
        }
      } else {
        toast.error(result.message);
      }
      setUploading(false);
      navigate("/products/tiles");
    } catch (error) {
      let message = "Failed to upload PDF. Please try again.";
      if (error.response && error.response.data) {
        message =
          error.response.data.name.length > 0
            ? error.response.data.name[0]
            : message;
      }
      toast.error(message);
      console.error("Error fetching data:", error);
      setUploading(false);
    } finally {
      setUploading(false);
    }
  };
  useEffect(() => {
    fetchTile();
    fetchPdfs();
  }, [product_id]);

  useEffect(() => {
    if (tile) {
      fetchTile();
    }
  }, [hideFirm]);

  if (loading) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h4 className="card-title">Manage PDFs & Links - {tile?.name}</h4>
            <div className="card-actions">
              <Link to={`/products/tiles/`} className="btn btn-secondary">
                Back to Tiles
              </Link>
            </div>
          </div>
          <div className="card-body">
            {/* Toggle Buttons */}
            <div className="row mb-4">
              <div className="col-md-12">
                <div className="btn-group w-100" role="group">
                  <button
                    type="button"
                    className={`btn ${activeSection === "pdf" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => handleSectionChange("pdf")}
                  >
                    <i className="fas fa-file-pdf me-2"></i>
                    PDF Upload
                  </button>
                  <button
                    type="button"
                    className={`btn ${activeSection === "link" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => handleSectionChange("link")}
                  >
                    <i className="fas fa-link me-2"></i>
                    Link Management
                  </button>
                </div>
              </div>
            </div>

            {/* PDF Upload Section */}
            <div className="row mb-4" style={{ display: activeSection === "pdf" ? "block" : "none" }}>
              <div className="col-md-12">
                <div className="card">
                  <div className="card-header">
                    <h5 className="card-title mb-0">PDF Upload</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-4">
                        <div className="form-group mb-3">
                          <label className="form-label">PDF File</label>
                          <div className="input-group">
                            <input
                              ref={fileInputRef}
                              type="file"
                              className="form-control"
                              id="pdf-files"
                              accept=".pdf"
                              disabled={pdfs.length >= MAX_PRODUCT_PDF_COUNT}
                              onChange={handleFileChange}
                              required
                            />
                          </div>
                          <small className="form-text text-muted">
                            Supported formats: PDF (Max size:{" "}
                            {MAX_PDF_FILE_SIZE}MB per file)
                          </small>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="d-flex align-items-center justify-content-between">
                          <label className="form-label mb-1">File Name</label>
                          <label className="ms-2 float-end">
                            <input
                              type="checkbox"
                              checked={hideFirm}
                              onChange={() => setHideFirm(!hideFirm)}
                              id="hideFirm"
                            />{" "}
                            Hide Firm Name
                          </label>
                        </div>
                        <div className="input-group">
                          <span className="input-group-text">
                            {defaultFileName}
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            name="fileName"
                            value={fileName}
                            placeholder="File Name"
                            onChange={(e) => setFileName(e.target.value)}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <label className="form-label w-100">&nbsp;</label>
                          <button
                            type="button"
                            onClick={saveUpload}
                            className="btn btn-primary"
                            disabled={
                              uploading ||
                              !fileName ||
                              !selectedFile ||
                              pdfs.length >= MAX_PRODUCT_PDF_COUNT
                            }
                          >
                            {uploading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Uploading...
                              </>
                            ) : pdfs.length >= MAX_PRODUCT_PDF_COUNT ? (
                              "Maximum PDFs Reached"
                            ) : (
                              "Upload and Publish"
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Link Management Section */}
            <div className="row mb-4" style={{ display: activeSection === "link" ? "block" : "none" }}>
              <div className="col-md-12">
                <div className="card">
                  <div className="card-header">
                    <h5 className="card-title mb-0">Link Management</h5>
                  </div>
                  <div className="card-body">
                    {/* Link Input Form */}
                    <div className="row align-items-center mb-3">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">Link URL</label>
                          <input
                            type="url"
                            className="form-control"
                            placeholder="https://example.com"
                            value={linkUrl}
                            onChange={(e) => setLinkUrl(e.target.value)}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="d-flex align-items-end">
                          <button
                            type="button"
                            onClick={saveLink}
                            className="btn btn-primary me-2"
                            disabled={uploading || !linkUrl.trim()}
                          >
                            {uploading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Saving...
                              </>
                            ) : tile?.link ? (
                              "Update Link"
                            ) : (
                              "Add Link and Publish"
                            )}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleCloseLinkModal}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                    {tile?.link && (
                      <div className="row align-items-center">
                        <div className="col-md-8">
                          <div className="d-flex align-items-center">
                            <i className="fas fa-link text-primary me-2"></i>
                            <a
                              href={tile.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary me-3"
                            >
                              {tile.link}
                            </a>
                          </div>
                        </div>
                        <div className="col-md-4 text-end">
                          <button
                            type="button"
                            className="btn btn-primary btn-sm me-2"
                            onClick={handleLinkIconClick}
                          >
                            <i className="fas fa-edit me-1"></i>
                            Update Link
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={handleDeleteLink}
                          >
                            <i className="fas fa-trash me-1"></i>
                            Delete Link
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TilePdfs;
